import { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { HiOutlineChevronDown, HiOutlineSearch, HiOutlineX } from "react-icons/hi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import DateTimePicker from "../../components/DateTimePicker";
import {
  createRenewalDiscussion,
  updateRenewalPreliminary,
  getRenewalDiscussionDetails,
  getRenewalNextLeadNumber,
  type CreateRenewalDiscussionPayload,
  type UpdateRenewalPreliminaryPayload,
  type RenewalInitiationType,
} from "../../api/renewal";
import {
  listAcknowledgedApplications,
  type NewApplicationListItem,
} from "../../api/applications";
import { toast } from "sonner";
import { normalizePhone, formatPhoneInput } from "../../utils/phone";

export const RENEWAL_PRELIMINARY_FORM_ID = "renewal-preliminary-form";

type FormValues = {
  leadNo: string;
  markName: string;
  contactName: string;
  phone: string;
  email: string;
  callTime: Date | null;
  initiationType: RenewalInitiationType;
  referenceLeadId: string;
};

const initialValues: FormValues = {
  leadNo: "",
  markName: "",
  contactName: "",
  phone: "",
  email: "",
  callTime: null,
  initiationType: "CLIENT_INITIATED",
  referenceLeadId: "",
};

const CREATE_FIELDS: (keyof CreateRenewalDiscussionPayload)[] = [
  "markName",
  "contactName",
  "phone",
  "email",
  "callTime",
  "initiationType",
  "referenceLeadId",
];

function pickCreatePayload(values: FormValues): CreateRenewalDiscussionPayload {
  const payload: Record<string, unknown> = {};
  for (const key of CREATE_FIELDS) {
    const v = values[key as keyof FormValues];
    if (key === "email" && (v === undefined || v === "")) continue;
    if (key === "referenceLeadId") {
      if (values.initiationType === "EXISTING_LEAD" && typeof v === "string" && v.trim()) {
        payload[key] = v.trim();
      }
      continue;
    }
    if (key === "callTime" && v instanceof Date) {
      payload[key] = v.toISOString();
      continue;
    }
    if (v !== undefined && v !== null && v !== "") {
      payload[key] = v;
    }
  }
  return payload as unknown as CreateRenewalDiscussionPayload;
}

const UPDATE_FIELDS: (keyof UpdateRenewalPreliminaryPayload)[] = [
  "markName",
  "contactName",
  "phone",
  "email",
  "callTime",
];

function pickUpdatePayload(values: FormValues): UpdateRenewalPreliminaryPayload {
  const payload: Record<string, unknown> = {};
  for (const key of UPDATE_FIELDS) {
    const v = values[key as keyof FormValues];
    if (key === "email" && (v === undefined || v === "")) continue;
    if (key === "callTime" && v instanceof Date) {
      payload[key] = v.toISOString();
      continue;
    }
    if (v !== undefined && v !== null && v !== "") {
      payload[key] = v;
    }
  }
  return payload as unknown as UpdateRenewalPreliminaryPayload;
}

const validationSchema = Yup.object({
  leadNo: Yup.string().required("Required"),
  markName: Yup.string().required("Mark is required"),
  contactName: Yup.string().required("Contact name is required"),
  phone: Yup.string()
    .required("WhatsApp Number is required")
    .test("mobile", "Enter a valid mobile number (10–15 digits, optional + prefix)", (val) => {
      if (!val?.trim()) return false;
      const digits = normalizePhone(val);
      return digits.length >= 10 && digits.length <= 15;
    }),
  email: Yup.string().email("Invalid email").required("Email is required"),
  callTime: Yup.date()
    .required("Time & Date of call is required")
    .nullable()
    .typeError("Please select a valid date and time"),
  initiationType: Yup.string()
    .oneOf(["CLIENT_INITIATED", "EXISTING_LEAD"])
    .required("Initiation type is required"),
  referenceLeadId: Yup.string().when("initiationType", {
    is: "EXISTING_LEAD",
    then: (s) => s.required("Reference Lead ID is required for Existing Lead"),
    otherwise: (s) => s.optional(),
  }),
});

type Props = {
  leadId?: string;
  onSuccess?: (leadId: string) => void;
  onSubmittingChange?: (submitting: boolean) => void;
};

function toFormValues(details: {
  leadNo: string;
  initiationType: string | null;
  referenceLeadId: string | null;
  preliminary: {
    markName: string | null;
    contactName: string;
    phone: string | null;
    email: string | null;
    callTime: string | null;
  };
}): FormValues {
  return {
    leadNo: details.leadNo ?? "",
    markName: details.preliminary.markName ?? "",
    contactName: details.preliminary.contactName ?? "",
    phone: details.preliminary.phone ?? "",
    email: details.preliminary.email ?? "",
    callTime: details.preliminary.callTime ? new Date(details.preliminary.callTime) : null,
    initiationType: (details.initiationType as RenewalInitiationType) ?? "CLIENT_INITIATED",
    referenceLeadId: details.referenceLeadId ?? "",
  };
}

// ---------------------------------------------------------------------------
// Searchable picker for selecting an existing new-application lead.
// ---------------------------------------------------------------------------

function ExistingLeadPicker({
  selectedId,
  selectedLabel,
  onSelect,
  onClear,
}: {
  selectedId: string;
  selectedLabel: string;
  onSelect: (lead: NewApplicationListItem) => void | Promise<void>;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [leads, setLeads] = useState<NewApplicationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await listAcknowledgedApplications({
          page: 1,
          limit: 20,
          search: search.trim() || undefined,
        });
        if (!cancelled) setLeads(data.items);
      } catch {
        if (!cancelled) setLeads([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, search]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const displayLabel = selectedLabel || (selectedId ? selectedId : "");

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
      >
        <span className={`truncate ${displayLabel ? "text-[#222]" : "text-[#6D6D6D]"}`}>
          {displayLabel || "Select existing lead"}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selectedId && (
            <span
              role="button"
              aria-label="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              className="p-0.5 hover:bg-[#F0F0F0] rounded"
            >
              <HiOutlineX className="w-3.5 h-3.5 text-[#6D6D6D]" />
            </span>
          )}
          <HiOutlineChevronDown className="w-4 h-4 text-[#6D6D6D]" />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#EEE] rounded-[6px] shadow-md flex flex-col max-h-[280px]">
          <div className="flex items-center gap-2 px-2.5 py-2 border-b border-[#EEE]">
            <HiOutlineSearch className="w-3.5 h-3.5 text-[#6D6D6D] flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by lead no, mark, contact…"
              autoFocus
              className="flex-1 min-w-0 text-[12px] text-[#222] placeholder:text-[#6D6D6D] focus:outline-none bg-transparent"
            />
            {loading && (
              <span
                className="w-3.5 h-3.5 border-2 border-[#EEE] border-t-[#222] rounded-full animate-spin flex-shrink-0"
                aria-hidden
              />
            )}
          </div>
          <div className="flex-1 overflow-y-auto min-h-[60px]">
            {leads.length === 0 ? (
              <div className="px-3 py-3 text-[11px] text-[#6D6D6D]">
                {loading ? "Searching…" : "No leads found."}
              </div>
            ) : (
              leads.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={async () => {
                    setOpen(false);
                    await onSelect(l);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-[#F7F7F7] flex flex-col gap-0.5 border-b border-[#F0F0F0] last:border-b-0"
                >
                  <span className="text-[12px] font-medium text-[#222] tabular-nums">
                    {l.leadNo}
                  </span>
                  <span className="text-[11px] text-[#6D6D6D] truncate">
                    {l.markName ?? "(no mark)"} • {l.clientName || "(no contact)"}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RenewalPreliminaryDiscussionView({ leadId, onSuccess, onSubmittingChange }: Props) {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadedValues, setLoadedValues] = useState<FormValues | null>(null);
  const [referenceLeadLabel, setReferenceLeadLabel] = useState<string>("");

  useEffect(() => {
    return () => onSubmittingChange?.(false);
  }, [onSubmittingChange]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setLoadedValues(null);
    setReferenceLeadLabel("");

    type LoadResult = { values: FormValues; referenceLeadLabel: string };

    const load: Promise<LoadResult> = leadId
      ? getRenewalDiscussionDetails(leadId).then((d) => {
          const values = toFormValues(d);
          let label = "";
          if (d.referenceLead?.leadNo) {
            label = d.referenceLead.leadNo;
          }
          return { values, referenceLeadLabel: label };
        })
      : getRenewalNextLeadNumber().then(({ leadNo }) => ({
          values: { ...initialValues, leadNo },
          referenceLeadLabel: "",
        }));

    load
      .then((result) => {
        if (!cancelled) {
          setLoadedValues(result.values);
          setReferenceLeadLabel(result.referenceLeadLabel);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : "Failed to load";
          toast.error(msg);
          setLoadError(msg);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [leadId]);

  const isReady = loadedValues !== null || loadError !== null;

  if (!isReady) {
    return (
      <div className="max-w-[1085px] flex flex-col gap-5">
        <div className="text-xs text-[#6D6D6D]">
          {leadId ? "Loading lead details…" : "Loading next lead number…"}
        </div>
      </div>
    );
  }

  const startValues = loadedValues ?? initialValues;
  const isEdit = Boolean(leadId);

  return (
    <Formik
      key={leadId ?? "new"}
      initialValues={startValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        onSubmittingChange?.(true);
        try {
          if (isEdit && leadId) {
            const payload = pickUpdatePayload(values);
            await updateRenewalPreliminary(leadId, payload);
            toast.success("Preliminary discussion updated successfully.");
            onSuccess?.(leadId);
          } else {
            const payload = pickCreatePayload(values);
            const data = await createRenewalDiscussion(payload);
            toast.success("Renewal preliminary discussion started successfully.");
            onSuccess?.(data.leadId);
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Failed to save.";
          toast.error(msg);
        } finally {
          setSubmitting(false);
          onSubmittingChange?.(false);
        }
      }}
    >
      {({ setFieldValue, values, errors, touched, submitCount }) => {
        const showErr = (k: keyof FormValues): boolean =>
          Boolean(errors[k]) && (Boolean(touched[k]) || submitCount > 0);
        const errBorder = (k: keyof FormValues): string =>
          showErr(k) ? " !border-red-500" : "";
        return (
          <Form
            id={RENEWAL_PRELIMINARY_FORM_ID}
            className="max-w-[1085px] flex flex-col gap-5"
          >
            {/* Row 1: Initiation Type + Reference Lead */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-5">
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-normal text-[#222] leading-5">Initiation Type</label>
                {isEdit ? (
                  <div className="w-full px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] bg-[#F5F5F5] text-[#6D6D6D] cursor-not-allowed">
                    {values.initiationType === "CLIENT_INITIATED" ? "Client Initiated" : "Existing Lead"}
                  </div>
                ) : (
                  <Select
                    value={values.initiationType}
                    onValueChange={(val) => {
                      setFieldValue("initiationType", val as RenewalInitiationType);
                      if (val === "CLIENT_INITIATED") setFieldValue("referenceLeadId", "");
                    }}
                  >
                    <SelectTrigger className={`w-full h-auto px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] bg-white focus:ring-1 focus:ring-[#222]${errBorder("initiationType")}`}>
                      <SelectValue placeholder="Select initiation type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CLIENT_INITIATED">Client Initiated</SelectItem>
                      <SelectItem value="EXISTING_LEAD">Existing Lead</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              {values.initiationType === "EXISTING_LEAD" && (
                <div className="flex flex-col gap-1">
                  <label className="text-[12px] font-normal text-[#222] leading-5">Reference Lead</label>
                  <ExistingLeadPicker
                    selectedId={values.referenceLeadId}
                    selectedLabel={referenceLeadLabel}
                    onClear={() => {
                      setFieldValue("referenceLeadId", "");
                      setReferenceLeadLabel("");
                    }}
                    onSelect={async (lead) => {
                      setFieldValue("referenceLeadId", lead.id);
                      setReferenceLeadLabel(
                        lead.markName ? `${lead.leadNo} (${lead.markName})` : lead.leadNo
                      );
                      setFieldValue("markName", lead.markName ?? "");
                      setFieldValue("contactName", lead.clientName ?? "");
                      setFieldValue("phone", lead.phone ?? "");
                      setFieldValue("email", lead.email ?? "");
                    }}
                  />
                </div>
              )}
            </div>

            {/* Row 2: Lead No + Mark */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-5">
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-normal text-[#6D6D6D] leading-5">Lead No.</label>
                <Field
                  name="leadNo"
                  type="text"
                  readOnly
                  className="w-full px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] focus:outline-none bg-white cursor-not-allowed md:bg-[#F5F5F5]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-normal text-[#222] leading-5">Mark</label>
                <Field
                  name="markName"
                  type="text"
                  placeholder="Text Here"
                  className={`w-full px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]${errBorder("markName")}`}
                />
              </div>
            </div>

            {/* Row 3: Contact Name + WhatsApp */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-5">
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-normal text-[#222] leading-5">Contact Name</label>
                <Field
                  name="contactName"
                  type="text"
                  placeholder="Text Here"
                  className={`w-full px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]${errBorder("contactName")}`}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-normal text-[#222] leading-5">WhatsApp Number</label>
                <Field name="phone">
                  {({ field, form }: { field: { name: string; value: string }; form: { setFieldValue: (n: string, v: string) => void } }) => (
                    <input
                      {...field}
                      type="tel"
                      inputMode="numeric"
                      placeholder="Enter WhatsApp Number"
                      className={`w-full px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]${errBorder("phone")}`}
                      onChange={(e) => form.setFieldValue("phone", formatPhoneInput(e.target.value))}
                    />
                  )}
                </Field>
              </div>
            </div>

            {/* Row 4: Email + Call Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-5">
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-normal text-[#222] leading-5">Email Address</label>
                <Field
                  name="email"
                  type="email"
                  placeholder="Email Address"
                  className={`w-full px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]${errBorder("email")}`}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-normal text-[#222] leading-5">
                  Time & Date of call <span className="text-red-500">*</span>
                </label>
                <DateTimePicker
                  value={values.callTime}
                  onChange={(date) => setFieldValue("callTime", date)}
                  showTime
                  placeholder="Select Date"
                  buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px]"
                  error={showErr("callTime")}
                  minDate={(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })()}
                />
                <ErrorMessage
                  name="callTime"
                  component="div"
                  className="text-xs text-red-500 mt-0.5"
                />
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}
